import { createRouter, createWebHistory } from 'vue-router';
import PageContent from './components/page-content.vue';
import PageProducts from './components/page-products.vue';
import PageOrder from './components/page-order.vue';
import PageOrderDetails from './components/page-order-details.vue';
import store from './store';
import { updateRouteSeo } from './utils/seo';
import { gtagPageView } from './analytics/gtag';
import { fbTrackPageView } from './analytics/facebook-pixel';

const homeMeta = {
    title: {
        bg: 'Икономичен диспенсър за опаковъчно тиксо AVEM | Произведено в България',
        en: 'Cost-Effective Packing Tape Dispenser AVEM | Made in Bulgaria',
    },
    description: {
        bg: 'AVEM е най-евтината и компактна машинка за тиксо (клипс и резачка за ленти до 50 мм). Патентован български продукт с тегло само 15 грама!',
        en: 'AVEM is the most compact and affordable packing tape dispenser (clip and cutter for tapes up to 50 mm). Patented product weighing only 15 grams!',
    },
    keywords: {
        bg: 'тиксо машинка, диспенсър за тиксо, опаковъчно тиксо, евтина машинка за пакетиране, резачка за тиксо, авем, avem',
        en: 'tape dispenser, packing tape dispenser, packaging cutter, adhesive tape dispenser, lightweight tape gun, avem',
    },
    ogTitle: {
        bg: 'Българската машинка за тиксо AVEM - Само за 3.53 €',
        en: 'AVEM Tape Dispenser - Only for 3.53 €',
    },
    ogDescription: {
        bg: 'Спести място и пари с най-компактния диспенсър за пакетиране на пазара.',
        en: 'Save space and money with the most compact packing dispenser on the market.',
    },
    image: 'https://avembg.com/img/tape_dispenser_new.jpg',
    robots: 'index, follow',
};

const offersMeta = {
    title: {
        bg: 'Продукти - AVEM | Тиксо машинки и лепящи ленти',
        en: 'Products - AVEM | Tape Dispensers & Tapes',
    },
    description: {
        bg: 'Разгледайте офертите за машинка за тиксо AVEM, промоционални пакети и ролки тиксо с високо качество. Поръчайте онлайн на топ цена!',
        en: 'Explore offers for AVEM tape dispensers, promotional bundles, and high quality packing tapes. Order online at great prices!',
    },
    keywords: {
        bg: 'оферти тиксо, промоция машинка за тиксо, тиксо машинка, ролки тиксо, avem, диспенсър',
        en: 'tape offers, tape dispenser promo, packing tape, tape rolls, avem, tape gun',
    },
    ogTitle: {
        bg: 'Машинки за тиксо и лепящи ленти AVEM - Оферти и промоции',
        en: 'AVEM Tape Dispensers & Adhesive Tapes - Offers & Promos',
    },
    ogDescription: {
        bg: 'Разгледайте всички актуални оферти и промоционални пакети на AVEM.',
        en: 'Explore all current offers and promotional bundles from AVEM.',
    },
    image: 'https://avembg.com/img/promo_dispenser_and_tape.jpg',
    robots: 'index, follow',
};

const orderMeta = {
    title: {
        bg: 'Поръчка | AVEM',
        en: 'Checkout | AVEM',
    },
    description: {
        bg: 'Завършете вашата поръчка на тиксо диспенсър и консумативи от онлайн магазин AVEM.',
        en: 'Complete your order for AVEM tape dispenser and accessories.',
    },
    robots: 'noindex, nofollow',
};

const orderDetailsMeta = {
    title: {
        bg: 'Детайли за поръчката | AVEM',
        en: 'Order Details | AVEM',
    },
    description: {
        bg: 'Детайли и потвърждение за направената поръчка от AVEM.',
        en: 'Order details and confirmation from AVEM.',
    },
    robots: 'noindex, nofollow',
};

const routes = [
    // Bulgarian (default) routes
    { path: '/', name: 'home', component: PageContent, meta: { ...homeMeta, lang: 'bg', canonical: 'https://avembg.com/' } },
    { path: '/bg', redirect: '/' },
    { path: '/offers', name: 'offers', component: PageProducts, meta: { ...offersMeta, lang: 'bg', canonical: 'https://avembg.com/offers' } },
    { path: '/bg/offers', redirect: '/offers' },
    { path: '/products', redirect: '/offers' },
    { path: '/order', name: 'order', component: PageOrder, meta: { ...orderMeta, lang: 'bg', canonical: 'https://avembg.com/order' } },
    { path: '/bg/order', redirect: '/order' },
    { path: '/order-details', name: 'order-details', component: PageOrderDetails, meta: { ...orderDetailsMeta, lang: 'bg', canonical: 'https://avembg.com/order-details' } },
    { path: '/bg/order-details', redirect: '/order-details' },

    // English (/en) routes
    { path: '/en', name: 'home-en', component: PageContent, meta: { ...homeMeta, lang: 'en', canonical: 'https://avembg.com/en' } },
    { path: '/en/offers', name: 'offers-en', component: PageProducts, meta: { ...offersMeta, lang: 'en', canonical: 'https://avembg.com/en/offers' } },
    { path: '/en/products', redirect: '/en/offers' },
    { path: '/en/order', name: 'order-en', component: PageOrder, meta: { ...orderMeta, lang: 'en', canonical: 'https://avembg.com/en/order' } },
    { path: '/en/order-details', name: 'order-details-en', component: PageOrderDetails, meta: { ...orderDetailsMeta, lang: 'en', canonical: 'https://avembg.com/en/order-details' } },

    // Catch-all route for unrecognized paths
    { path: '/:pathMatch(.*)*', redirect: '/' },
];

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (to.query.product || (to.hash && to.hash.startsWith('#product-'))) {
            return false;
        }
        if (to.hash) {
            if (to.hash === '#hero') {
                return { top: 0, behavior: 'smooth' };
            }
            return new Promise((resolve) => {
                const checkElement = () => {
                    const targetElement = document.querySelector(to.hash);
                    if (targetElement) {
                        const headerHeight = window.innerWidth < 600 ? 56 : (window.innerWidth < 960 ? 64 : 76);
                        const rect = targetElement.getBoundingClientRect();
                        const topPos = window.pageYOffset + rect.top - headerHeight;
                        resolve({
                            top: Math.max(0, topPos),
                            behavior: 'smooth',
                        });
                    } else {
                        setTimeout(checkElement, 50);
                    }
                };
                checkElement();
            });
        }

        if (savedPosition) {
            return savedPosition;
        }

        return { top: 0, behavior: 'smooth' };
    },
});

router.beforeEach((to, from, next) => {
    const isEn = to.path === '/en' || to.path.startsWith('/en/');
    const targetLang = isEn ? 'en' : 'bg';

    if (store && store.state && store.state.languageCode !== targetLang) {
        store.commit('setLanguageCode', targetLang);
    }
    next();
});

router.afterEach((to, from) => {
    const isEn = to.path === '/en' || to.path.startsWith('/en/');
    updateRouteSeo(to, isEn ? 'en' : 'bg');

    // Track SPA virtual page views on client-side route transitions
    if (from && from.matched && from.matched.length > 0 && to.fullPath !== from.fullPath) {
        gtagPageView(to.fullPath, document.title);
        fbTrackPageView();
    }
});

export default router;
